import React, { useEffect, useState } from 'react'
import { usePersistedState } from '../../../hooks/usePersistedState'
import { useIntl } from 'react-intl'
import { useSearchParams } from 'react-router-dom'
import styled from '@emotion/styled'
import { Box, LinearProgress, TextField } from '@mui/material'
import { H3 } from '../../../layouts/TextStyles'
import VideoWatchersTable from './components/VideoWatchersTable'
import { curatorStudentsApiSlice } from '../../../features/curator/curatorStudents'
import { courseSubjectApiSlice } from '../../../features/course/courseSubjectSlice'
import { courseTopicApiSlice } from '../../../features/course/courseTopicSlice'
import { sectionApiSlice } from '../../../features/section/sectionSlice'
import { videoLessonApiSlice } from '../../../features/section/videoLessonSlice'
import Sentinel from '../../../components/infiniteScroll/Sentinel'
import { usePaginatedList } from '../../../app/hooks/usePaginatedList'
import { capitalize } from '../../../utils/functions'
import MonthWeekFilter from '../../../components/monthWeekFilter/MonthWeekFilter'
import FilterSelect, { filterField, filterLabel } from '../../../components/filterSelect/FilterSelect'
import { ui } from '../../../utils/uiTokens'
import { useGroupLabel } from '../../head/headScope'
import DirectorSubjectFilter from '../../director/DirectorSubjectFilter'

const toYmd = (d: Date) => d.toISOString().slice(0, 10)

// Kinescope считает `to` исключающей границей полуночи: чтобы включить выбранный
// день, отправляем следующий (from=21 to=21 => уходит to=22).
const addDay = (ymd: string) => {
    const d = new Date(ymd + 'T00:00:00Z')
    d.setUTCDate(d.getUTCDate() + 1)
    return d.toISOString().slice(0, 10)
}

const VideoAnalysisPage = () => {
    const { formatMessage, locale } = useIntl()
    const groupLabel = useGroupLabel()
    const [searchParams, setSearchParams] = useSearchParams('')

    const [courseId, setCourseId] = useState<number | undefined>(undefined)
    const [courseSubjectId, setCourseSubjectId] = useState<number | undefined>(undefined)
    const [courseTopicId, setCourseTopicId] = useState<number | undefined>(undefined)
    const [videoLessonId, setVideoLessonId] = useState<number | undefined>(undefined)
    const [groupId, setGroupId] = useState<number | undefined>(undefined)
    const [monthNumber, setMonthNumber] = usePersistedState<number | undefined>('curator.monthNumber', undefined)
    const [weekNumber, setWeekNumber] = usePersistedState<number | undefined>('curator.weekNumber', undefined)
    // Тема заведена отдельной строкой на каждый язык, поэтому без выбора языка
    // в списке лежат обе версии одного урока
    const [language, setLanguage] = useState(locale.toUpperCase())
    const [generalVideos, setGeneralVideos] = useState<any[]>([])

    const [from, setFrom] = useState(() => {
        const d = new Date()
        d.setDate(d.getDate() - 30)
        return toYmd(d)
    })
    const [to, setTo] = useState(() => toYmd(new Date()))

    const { data: courseResponse } = curatorStudentsApiSlice.useFetchCuratorCoursesQuery()
    const { data: groups = [] } = curatorStudentsApiSlice.useFetchCuratorGroupsQuery(courseId, {
        skip: !courseId,
    })
    // Куратор видит только свои предметы курса; если предмет один - выбираем его сразу
    const { data: courseSubjects = [] } =
        courseSubjectApiSlice.useFetchCuratorCourseSubjectsQuery(courseId, { skip: !courseId })
    const { data: courseTopics = [] } = courseTopicApiSlice.useFetchActiveCourseTopicsQuery(
        courseSubjectId,
        { skip: !courseSubjectId },
    )
    const { data: sections = [] } = sectionApiSlice.useGetSectionsByCourseTopicQuery(
        courseTopicId,
        { skip: !courseTopicId },
    )
    const [fetchSections] = sectionApiSlice.useLazyGetSectionsByCourseTopicQuery()

    const [fetchWatchers] = videoLessonApiSlice.useLazyGetVideoWatchersAnalysisQuery()

    const { rows, isLoading: isFetching, loadedPages, sentinelRef } =
        usePaginatedList(
            (page, size) =>
                fetchWatchers({
                    videoLessonId,
                    from,
                    to: addDay(to),
                    groupId,
                    page,
                    size,
                }).unwrap(),
            [videoLessonId, from, to, groupId],
            { skip: !videoLessonId },
        )

    const toVideos = (list: any[], topicTitle?: string) =>
        (list || [])
            .filter((section: any) => section.type === 'videoLesson')
            .map((section: any) => ({
                id: section.id,
                name: topicTitle ? `${topicTitle} - ${section.title}` : section.title,
                duration: section.duration || 0,
            }))

    // Тема выбрана - её уроки, иначе общий список уроков выбранного периода
    const videos = courseTopicId ? toVideos(sections) : generalVideos

    // Язык и период сужают список тем: в нём остаются только нужные уроки
    const filteredTopics = (courseTopics || []).filter(
        (topic) =>
            (!language || topic.language === language) &&
            (!monthNumber || topic.monthNumber === monthNumber) &&
            (!weekNumber || topic.weekNumber === weekNumber),
    )

    const topics = filteredTopics.map((topic) => ({
        id: topic.courseTopicId,
        titleKz: topic.titleKz,
        titleRu: topic.titleRu,
    }))

    useEffect(() => {
        if (courseSubjects.length === 1 && !courseSubjectId) {
            setCourseSubjectId(courseSubjects[0].id)
        }
    }, [courseSubjects, courseSubjectId])

    // Без месяца тем слишком много, чтобы тянуть разделы по каждой,
    // поэтому общий список собираем только для выбранного периода
    useEffect(() => {
        if (courseTopicId || !monthNumber || !filteredTopics.length) {
            setGeneralVideos([])
            return
        }

        let cancelled = false

        Promise.all(
            filteredTopics.map((topic) =>
                fetchSections(topic.courseTopicId, true)
                    .unwrap()
                    .then((list) => toVideos(list, topic[`title${capitalize(locale)}`]))
                    .catch(() => []),
            ),
        ).then((lists) => {
            if (!cancelled) setGeneralVideos(lists.flat())
        })

        return () => {
            cancelled = true
        }
    }, [courseTopicId, monthNumber, weekNumber, language, courseSubjectId, courseTopics])

    // Каждый выбор сверху обнуляет всё, что под ним, иначе на экране остаётся
    // видео из другого предмета
    const handleCourseChange = (value: number) => {
        setCourseId(value)
        setCourseSubjectId(undefined)
        setCourseTopicId(undefined)
        setVideoLessonId(undefined)
        setGroupId(undefined)
    }

    const handleСourseSubjectChange = (value: number) => {
        setCourseSubjectId(value)
        setCourseTopicId(undefined)
        setVideoLessonId(undefined)
    }

    // Смена языка или периода делает выбранные тему и урок бессмысленными
    const resetSelection = () => {
        setCourseTopicId(undefined)
        setVideoLessonId(undefined)
    }

    const handlePeriodChange = (month?: number, week?: number) => {
        setMonthNumber(month)
        setWeekNumber(week)
        resetSelection()
    }

    const handleLanguageChange = (value: string) => {
        setLanguage(value)
        resetSelection()
    }

    const handleTopicChange = (value: number) => {
        setCourseTopicId(value)
        setVideoLessonId(undefined)
    }

    return (
        <Wrapper>
            <Content>
                <Header>
                    <Title>
                        <H3>{formatMessage({ id: 'videoAnalysis' })}</H3>
                    </Title>

                    <MonthWeekFilter
                        courseId={courseId ?? null}
                        courseSubjectId={courseSubjectId}
                        groupId={groupId}
                        monthNumber={monthNumber}
                        weekNumber={weekNumber}
                        onChange={handlePeriodChange}
                    />
                </Header>

                <Filters>
                    <DirectorSubjectFilter withLabel />
                    <FilterSelect
                        label={formatMessage({ id: 'course' })}
                        placeholder={formatMessage({ id: 'course' })}
                        value={courseId}
                        options={courseResponse?.data || []}
                        onChange={(value) => handleCourseChange(Number(value))}
                    />

                    <FilterSelect
                        label={formatMessage({ id: 'subject' })}
                        placeholder={formatMessage({ id: 'subject' })}
                        value={courseSubjectId}
                        options={courseSubjects}
                        disabled={!courseId}
                        onChange={(value) => handleСourseSubjectChange(Number(value))}
                    />

                    <FilterSelect
                        label={formatMessage({ id: 'language' })}
                        placeholder={formatMessage({ id: 'language' })}
                        value={language}
                        options={[
                            { id: 'KZ', name: 'Қазақша' },
                            { id: 'RU', name: 'Русский' },
                        ]}
                        onChange={handleLanguageChange}
                    />

                    <FilterSelect
                        label={formatMessage({ id: 'selectTopic' })}
                        placeholder={formatMessage({ id: 'selectTopic' })}
                        value={courseTopicId}
                        options={topics}
                        disabled={!courseSubjectId}
                        onChange={(value) => handleTopicChange(Number(value))}
                    />

                    <FilterSelect
                        label={formatMessage({ id: 'selectVideo' })}
                        placeholder={formatMessage({ id: 'selectVideo' })}
                        value={videoLessonId}
                        options={videos}
                        disabled={!courseTopicId && !monthNumber}
                        onChange={(value) => {
                            setVideoLessonId(Number(value))
                        }}
                    />

                    <FilterSelect
                        label={formatMessage({ id: 'group' })}
                        placeholder={formatMessage({ id: 'allGroups' })}
                        value={groupId}
                        options={groups.map((g) => ({ id: g.id, title: groupLabel(g) }))}
                        disabled={!courseId}
                        onChange={(value) => {
                            setGroupId(Number(value) || undefined)
                        }}
                    />

                    <DateRange>
                        <Box>
                            <Box sx={filterLabel}>{formatMessage({ id: 'dateFrom' })}</Box>
                            <TextField
                                type="date"
                                size="small"
                                fullWidth
                                value={from}
                                onChange={(e) => setFrom(e.target.value)}
                                InputProps={{ sx: filterField }}
                            />
                        </Box>

                        <Box>
                            <Box sx={filterLabel}>{formatMessage({ id: 'dateTo' })}</Box>
                            <TextField
                                type="date"
                                size="small"
                                fullWidth
                                value={to}
                                onChange={(e) => setTo(e.target.value)}
                                InputProps={{ sx: filterField }}
                            />
                        </Box>
                    </DateRange>
                </Filters>

                {isFetching && loadedPages === 0 ? (
                    <LinearProgress />
                ) : !videoLessonId ? (
                    <EmptyState>{formatMessage({ id: 'selectVideo' })}</EmptyState>
                ) : (
                    <Box mt={3}>
                        <VideoWatchersTable
                            watchers={rows}
                            isFetching={isFetching}
                        />

                        <Sentinel ref={sentinelRef} isLoading={isFetching} />
                    </Box>
                )}
            </Content>
        </Wrapper>
    )
}

export default VideoAnalysisPage

const Wrapper = styled.div`
    margin: 10px;
    border-radius: 16px;
`

const Content = styled.div`
    background-color: ${ui.card};
    border: 1px solid ${ui.border};
    border-radius: 16px;
    padding: 24px;
    min-height: 80vh;
`

const Header = styled.header`
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    align-items: center;
    justify-content: space-between;
`

const Title = styled.div`
    margin-bottom: 20px;
    text-align: left;
    display: flex;
    align-items: center;
    color: ${ui.ink};
`

const Filters = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
    gap: 16px;
    margin-bottom: 24px;
`

// Даты - одна пара: если строка не вмещает, переносим обе вместе
const DateRange = styled.div`
    grid-column: span 2;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;

    @media screen and (max-width: 600px) {
        grid-column: span 1;
        grid-template-columns: 1fr;
    }
`

const EmptyState = styled.div`
    padding: 64px 0;
    text-align: center;
    font-size: 14px;
    color: ${ui.muted};
`
